import { useState, useRef, useEffect, type FC } from "react";
import { xIcon } from "@progress/kendo-svg-icons";
import { SvgIcon } from "@progress/kendo-react-common";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { Popup } from "@progress/kendo-react-popup";
import { t } from "i18next";

export interface DropdownOption {
  label?: string;
  value: string;
}

interface GxFilterDropdownProps {
  label: string;
  data: (string | DropdownOption)[];
  value: string | null;
  onChange: (value: string | null) => void;
  className?: string;
}

const GxFilterDropdown: FC<GxFilterDropdownProps> = ({
  label,
  data,
  value,
  onChange,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [showClearIcon, setShowClearIcon] = useState(false);

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  const hasValue = value !== null && value !== "";

  const normalizedData = data.map((item) =>
    typeof item === "string"
      ? { value: item, label: item }
      : {
          value: item.value,
          label: item.label ?? item.value,
        },
  );

  const isDataEmpty = normalizedData.length === 0;

  const selected = normalizedData.find((item) => item.value === value);

  const getDisplayLabel = () => {
    if (!hasValue) return t(label);

    return selected?.label ?? value;
  };

  const handleSelect = (item: DropdownOption, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(item.value);
    setOpen(false);
  };

  const handleMouseEnter = () => {
    if (hasValue) {
      setShowClearIcon(true);
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setShowClearIcon(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        setOpen((prev) => !prev);
        break;

      case "Escape":
        setOpen(false);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        anchorRef.current &&
        !anchorRef.current.contains(target) &&
        popupRef.current &&
        !popupRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (!hasValue) {
      setShowClearIcon(false);
    }
  }, [hasValue]);

  return (
    <div className={className}>
      <div
        ref={anchorRef}
        className="gx-filter-dropdown"
        role="combobox"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
      >
        <Popup anchor={anchorRef.current} show={open}>
          <div
            ref={popupRef}
            className={`gx-filter-dropdown-tile ${
              isDataEmpty ? "flex items-center justify-center" : ""
            }`}
            role="listbox"
          >
            <ul>
              {!isDataEmpty ? (
                normalizedData.map((item, index) => (
                  <li
                    key={`${item.value}-${index}`}
                    role="option"
                    aria-selected={value === item.value}
                    className={`gx-filter-dropdown-tile-list ${
                      value === item.value ? "bg-[gainsboro]" : ""
                    }`}
                    onClick={(e) => handleSelect(item, e)}
                  >
                    <span className="gx-font-medium text-[0.875rem]">
                      {item.label}
                    </span>
                  </li>
                ))
              ) : (
                <div>{t("no_data")}</div>
              )}
            </ul>
          </div>
        </Popup>

        <p className={`gx-p12-b ${hasValue ? "text-black" : "text-[#7a7a7a]"}`}>
          {getDisplayLabel()}
        </p>

        {!showClearIcon ? (
          <img
            onMouseEnter={handleMouseEnter}
            width={17}
            className={`ms-1 transition-transform duration-300 ease-in-out ${
              open ? "-rotate-180" : "rotate-0"
            }`}
            src={`${ICON_BASE_PATH}down_arrow.svg`}
            alt=""
          />
        ) : (
          <div
            className="gx-accf-clear-btn"
            onMouseLeave={() => setShowClearIcon(false)}
            onClick={handleClear}
          >
            <SvgIcon icon={xIcon} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GxFilterDropdown;
